import React, { useState } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box, 
  Alert,
  CircularProgress,
  TextField,
  Divider
} from '@mui/material';
import { Google as GoogleIcon, Email as EmailIcon } from '@mui/icons-material';
import { 
  signInWithPopup, 
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  User 
} from 'firebase/auth';
import { auth, googleProvider } from '../Firebase/firebase.ts';
import { auditLog } from '../Firebase/auditLog.ts';
import { trackEvent } from '../Analytics/posthog.ts';
import BackgroundWrapper from '../components/BackgroundWrapper.tsx';
import './SignIn.css';

interface SignInProps {
  onSignIn?: (user: User) => void;
}

const SignIn: React.FC<SignInProps> = ({ onSignIn }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    // Track attempt
    trackEvent('signin_google_attempted');

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('User signed in:', user);
      
      // Track successful sign in
      trackEvent('signin_google_success', {
        email: user.email,
        displayName: user.displayName,
      });
      
      // Log successful login
      await auditLog.loginSuccess(user.email || '', user.displayName || '');
      
      if (onSignIn) {
        onSignIn(user);
      }
    } catch (error: any) {
      console.error('Error signing in:', error);
      setError(error.message || 'שגיאה בהתחברות');
      
      // Track failed sign in
      trackEvent('signin_google_failed', {
        error: error.message || 'Unknown error',
      });
      
      // Log failed login attempt
      await auditLog.loginFailure(error.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSignInLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Track attempt
    trackEvent('signin_email_link_attempted', { email });

    try {
      const actionCodeSettings = {
        url: window.location.origin + '/signin',
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      
      // Save the email locally so we can use it to complete sign-in
      window.localStorage.setItem('emailForSignIn', email);
      
      setLinkSent(true);
      console.log('Sign-in link sent to:', email);
      
      // Track successful email link send
      trackEvent('signin_email_link_sent', { email });
      
    } catch (error: any) {
      console.error('Error sending sign-in link:', error);
      let errorMessage = 'שגיאה בשליחת קישור ההתחברות';
      
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = 'כתובת מייל לא תקינה';
          break;
        case 'auth/user-disabled':
          errorMessage = 'חשבון המשתמש חסום';
          break;
        default:
          errorMessage = error.message || 'שגיאה בשליחת קישור ההתחברות';
      }
      
      // Track failed email link send
      trackEvent('signin_email_link_failed', {
        email,
        error: error.message || 'Unknown error',
        errorCode: error.code,
      });
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if the current URL is a sign-in link and handle it
  React.useEffect(() => {
    const handleSignInLink = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = window.localStorage.getItem('emailForSignIn');
        
        if (!email) {
          // If missing email, prompt user for it
          email = window.prompt('אנא הכנס את כתובת המייל שלך לאישור');
        }

        if (email) {
          try {
            const result = await signInWithEmailLink(auth, email, window.location.href);
            const user = result.user;
            
            // Clear the email from storage
            window.localStorage.removeItem('emailForSignIn');
            
            console.log('User signed in with email link:', user);
            
            // Track successful email link sign in
            trackEvent('signin_email_link_success', {
              email: user.email,
              displayName: user.displayName,
            });
            
            // Log successful login
            await auditLog.loginSuccess(user.email || '', user.displayName || '');
            
            if (onSignIn) {
              onSignIn(user);
            }
            
            // Clear the URL
            window.history.replaceState({}, document.title, '/signin');
            
          } catch (error: any) {
            console.error('Error signing in with email link:', error);
            setError('שגיאה בהתחברות עם הקישור');
            
            // Track failed email link sign in
            trackEvent('signin_email_link_completion_failed', {
              email,
              error: error.message || 'Unknown error',
            });
            
            // Log failed login attempt
            await auditLog.loginFailure(error.message || 'Unknown error', email);
          }
        }
      }
    };

    handleSignInLink();
  }, [onSignIn]);

  return (
    <BackgroundWrapper>
      <Container maxWidth="sm" sx={{ py: 8, direction: 'rtl' }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.95)'
          }}
        >
        <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
          התחברות למערכת
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {linkSent 
            ? 'נשלח קישור התחברות לכתובת המייל שלך. בדוק את תיבת הדואר שלך ולחץ על הקישור כדי להתחבר.'
            : 'הכנס את כתובת המייל שלך ונשלח לך קישור התחברות'
          }
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'right' }}>
            {error}
          </Alert>
        )}

        {/* Email Magic Link Form */}
        {!linkSent ? (
          <Box component="form" onSubmit={handleSendSignInLink} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="כתובת מייל"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 3 }}
              InputLabelProps={{ style: { textAlign: 'right' } }}
              placeholder="example@domain.com"
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={loading ? null : <EmailIcon />}
              disabled={loading}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                mb: 2
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  שולח קישור...
                </>
              ) : (
                'שלח קישור התחברות'
              )}
            </Button>
          </Box>
        ) : (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 3, textAlign: 'right' }}>
              קישור התחברות נשלח לכתובת: {email}
              <br />
              <br />
              <b>יש לבדוק גם בספאם !!!</b>
            </Alert>
            <Button
              variant="outlined"
              onClick={() => {
                setLinkSent(false);
                setError(null);
              }}
              sx={{ mb: 2 }}
            >
              שלח שוב
            </Button>
          </Box>
        )}

        {/* Divider */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            או
          </Typography>
        </Divider>

        {/* Google Sign In */}
        <Box sx={{ position: 'relative' }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={loading ? null : <GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={loading}
            fullWidth
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              borderColor: '#4285f4',
              color: '#4285f4',
              '&:hover': {
                borderColor: '#3367d6',
                backgroundColor: 'rgba(66, 133, 244, 0.04)',
              },
              '&:disabled': {
                borderColor: '#ccc',
                color: '#ccc',
              }
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                מתחבר...
              </>
            ) : (
              'התחבר עם Google'
            )}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          על ידי התחברות, אתה מסכים לתנאי השימוש של המערכת
        </Typography>
        </Paper>
      </Container>
    </BackgroundWrapper>
  );
};

export default SignIn;
